/**
 * The place list: the "Where we work" list on the hero, whose links the page
 * carries. Every place is also marked on the globe; hovering a name draws a line from it to its dot and
 * turns the globe towards the place. On phones there is no hover and the list sits below the fold, so
 * only the dots are drawn.
 */
import { glow } from '../draw'
import { smootherstep } from '../easing'
import type { Canvas2D, View } from '../renderer/renderer'
import type { Stop } from './stops'

/* =====================================================================
   TUNING
   ===================================================================== */
const NAV_LINE = 0.75 /* brightness of the line that ties a hovered name to its dot */
const NAV_DOT = 0.9 /* brightness of the dots marking the places, at rest */
/* ===================================================================== */

export interface PlaceListOptions {
  readonly el: HTMLElement
  readonly stops: Stop[]
  /** A name was clicked. */
  readonly jump: (stop: Stop) => void
  /** A name is being pointed at (or focused), or no longer is. */
  readonly point: (stop: Stop, on: boolean) => void
}

export function createPlaceList({ el, stops, jump, point }: PlaceListOptions) {
  let hovered = -1
  /* whether the page is at phone width, where a tap is not a hover */
  let mobile = false
  /* how far the line to each name has been drawn out, 0..1 */
  const hot: number[] = []

  stops.forEach((stop, i) => {
    /* the page carries a link to each stop, so the list works without scripts */
    const link = el.querySelector<HTMLAnchorElement>(`a[href="#${stop.el.id}"]`)
    if (!link) throw new Error(`the place list has no link to ${stop.el.id}`)
    stop.link = link
    function on() {
      hovered = i
      point(stop, true)
    }
    function off() {
      if (hovered === i) hovered = -1
      point(stop, false)
    }
    link.addEventListener('click', (e) => {
      e.preventDefault()
      jump(stop)
      point(stop, false)
    })
    link.addEventListener('pointerenter', on)
    link.addEventListener('focus', on)
    link.addEventListener('pointerleave', off)
    link.addEventListener('blur', off)
  })

  return {
    /** The stop whose name is being pointed at, if any. */
    get hovered(): Stop | undefined {
      return mobile ? undefined : stops[hovered]
    },
    /** No line left hanging from the name just clicked. */
    clearHover() {
      hovered = -1
      hot.length = 0
    },
    /**
     * Draws the dots on the globe and, for the hovered name, its line; `a` is how visible they are.
     * At phone width (`isMobile`) no name counts as hovered.
     */
    draw(ctx: Canvas2D, v: View, a: number, dt: number, flying: boolean, isMobile: boolean) {
      const { cx, cy, Rs } = v
      const k = 1 - Math.exp(-dt * 8)
      mobile = isMobile
      const hot_i = mobile ? -1 : hovered
      ctx.globalCompositeOperation = 'lighter'
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      el.classList.toggle('has-hot', hot_i >= 0)
      for (let i = 0; i < stops.length; i++) {
        const s = stops[i]
        const h = (hot[i] = (hot[i] || 0) + ((hot_i === i ? 1 : 0) - (hot[i] || 0)) * k)
        s.link!.classList.toggle('hot', hot_i === i)
        const r = s.link!.getBoundingClientRect()
        const lx = r.left - 10
        const ly = r.top + r.height / 2
        /* the dot: a real place, or for the world-wide stop the globe's edge facing its name */
        let dx: number
        let dy: number
        let vis: number
        if (s.global) {
          const ang = Math.atan2(ly - cy, lx - cx)
          dx = cx + Math.cos(ang) * Rs
          dy = cy + Math.sin(ang) * Rs
          vis = 1
        } else {
          dx = s.px
          dy = s.py
          vis = s.vis
        }
        const A = a * vis
        if (A < 0.01) continue
        /* at rest: just the place, marked */
        if (!s.global) {
          ctx.strokeStyle = `rgba(242,215,174,${(NAV_DOT * A * (0.55 + 0.45 * h)).toFixed(3)})`
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.arc(dx, dy, 4 + 2.5 * h, 0, 6.2832)
          ctx.stroke()
          ctx.fillStyle = `rgba(255,240,215,${(Math.min(1, NAV_DOT) * A).toFixed(3)})`
          ctx.beginPath()
          ctx.arc(dx, dy, 1.8, 0, 6.2832)
          ctx.fill()
        }
        /* on hover: the line drawn out from the name to its dot */
        if (h > 0.01 && !flying) {
          const ex = Math.min(Math.max(cx + Rs + 22, lx - 46), lx - 8)
          const ey = ly
          const L1 = Math.hypot(lx - ex, 0)
          const L2 = Math.hypot(ex - dx, ey - dy)
          const done = smootherstep(0, 1, h) * (L1 + L2)
          ctx.strokeStyle = `rgba(242,215,174,${(NAV_LINE * A * h).toFixed(3)})`
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(lx, ly)
          if (done <= L1) ctx.lineTo(lx - done, ly)
          else {
            ctx.lineTo(ex, ey)
            const f = (done - L1) / Math.max(L2, 1)
            ctx.lineTo(ex + (dx - ex) * f, ey + (dy - ey) * f)
          }
          ctx.stroke()
          glow(ctx, dx, dy, 24, 0.4 * h * A, 0.2, '242,215,174')
        }
      }
    },
  }
}

export type PlaceList = ReturnType<typeof createPlaceList>
